// Type for the API response from GetPawSupportInfo endpoint
interface GetPawSupportInfoResponse {
  intercomAppId: string
  intercomPawSupportAppId: string
}

// Module-level cache to persist across composable instances
const configCache = {
  generalAppId: null as string | null,
  pawSupportAppId: null as string | null,
  fetchPromise: null as Promise<{ generalAppId: string; pawSupportAppId: string }> | null,
  isFetching: false
}

/**
 * Composable for managing Intercom configuration
 * 
 * Fetches both general support and PawSupport Intercom app IDs from the backend API.
 * Provides caching to avoid repeated calls during the same session.
 */
export const useIntercomConfig = () => {

  /**
   * Fetch both Intercom app IDs from the backend API
   * Uses caching to avoid repeated calls during the same session
   */
  const getIntercomAppIds = async (): Promise<{ generalAppId: string; pawSupportAppId: string }> => {
    // Return cached values if available
    if (configCache.generalAppId && configCache.pawSupportAppId) {
      return {
        generalAppId: configCache.generalAppId,
        pawSupportAppId: configCache.pawSupportAppId
      }
    }

    // Return existing promise if already fetching
    if (configCache.fetchPromise) {
      return configCache.fetchPromise
    }

    // Prevent race condition: check synchronous flag before creating promise
    if (configCache.isFetching) {
      // Wait a bit and retry - another call is creating the promise
      await new Promise(resolve => setTimeout(resolve, 50))
      return getIntercomAppIds()
    }

    // Set synchronous flag immediately to prevent concurrent initialization
    configCache.isFetching = true

    // Create new fetch promise
    configCache.fetchPromise = (async () => {
      try {
        // Get the policy ID as required by the API
        const { policyId } = await useSelectedPolicy()
        
        // Validate policy ID before making API call
        if (!policyId) {
          throw new Error('No policy ID available')
        }
        
        if (typeof policyId !== 'string' || policyId.trim() === '') {
          throw new Error('Invalid policy ID format')
        }
        
        // Call the GetPawSupportInfo endpoint with policy ID
        const { response } = useApi('/pawsupport/GetPawSupportInfo', {
          query: { policyId }
        })

        const result = await response as GetPawSupportInfoResponse
        
        // Extract both app IDs from response
        const generalAppId = result.intercomAppId
        const pawSupportAppId = result.intercomPawSupportAppId
        
        if (!generalAppId || !pawSupportAppId) {
          throw new Error('Invalid response: missing Intercom app IDs')
        }
        
        if (typeof generalAppId !== 'string' || generalAppId.trim() === '' ||
            typeof pawSupportAppId !== 'string' || pawSupportAppId.trim() === '') {
          throw new Error('Invalid Intercom app ID format received from API')
        }
        
        configCache.generalAppId = generalAppId.trim()
        configCache.pawSupportAppId = pawSupportAppId.trim()
        
        return {
          generalAppId: configCache.generalAppId,
          pawSupportAppId: configCache.pawSupportAppId
        }
      } catch (error) {
        throw new Error(`Unable to load chat configuration: ${error instanceof Error ? error.message : 'Unknown error'}`)
      } finally {
        // Clear the promise and flag so it can be retried if needed
        configCache.fetchPromise = null
        configCache.isFetching = false
      }
    })()

    return configCache.fetchPromise
  }

  /**
   * Clear the cached app IDs
   */
  const clearCache = () => {
    configCache.generalAppId = null
    configCache.pawSupportAppId = null
    configCache.fetchPromise = null
    configCache.isFetching = false
  }

  return {
    getIntercomAppIds,
    clearCache
  }
}
